import { useEffect } from "react";
import {
  useExternalConnectionStatus,
  useConnectExternalConnection,
  useUpdateCronConfig,
  useSyncNowExternalConnection,
} from "@/utils/react-query-hooks/externalConnections";
import { useGetAllPublishedAndUnpublishedTpls } from "@/utils/react-query-hooks/tci";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { __CheckAccessToModule } from "@/utils/accessControl";
import {
  formatDateTime,
  nextAutoSyncForFrequency,
} from "../IntegrationSettingsModal";

// Hooks shared by the integration popups (see modals/).

// Disconnected (here or elsewhere): nothing left to show.
const useCloseOnDisconnect = (conn, isLoading, onClose) => {
  useEffect(() => {
    if (!isLoading && conn?.connected === false) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, conn?.connected]);
};

// Why a dataset's Sync Now is disabled, or null when it can run. Needs an
// active connection and an incremental cursor (seeded by the dataset's first
// successful import).
const syncBlockedReason = (link, { canPublish, isConnActive }) =>
  !canPublish
    ? "You don't have permission to run a sync."
    : !isConnActive
      ? "Connection is not active — reconnect first."
      : !link.cronConfig?.startSyncFrom
        ? "Run a manual import first to enable instant sync."
        : null;

// A dataset link from the status endpoint, in the shape the manage views use.
// No schedule (switched off, or auto-paused after failures) is a one-time import.
const toDataset = (
  link,
  title,
  { isSyncStarting, isSavingCron, ...access },
) => {
  const { enabled, frequency } = link.cronConfig || {};
  const { at, rowCount, source } = link.lastImport || {};
  return {
    id: link.contentType,
    title,
    schedule: enabled ? frequency || "daily" : "one-time",
    frequency: enabled ? frequency || "daily" : "off",
    // In flight if this tab just started one, or the link says one is running
    // (cron, another tab, or a sync still going after a reopen).
    syncing: !!link.syncInProgress || isSyncStarting,
    syncBlockedReason: syncBlockedReason(link, access),
    savingSchedule: isSavingCron,
    // Set when repeated failures switched the schedule off.
    autoPausedReason: link.autoPausedReason,
    // What the latest manual import picked (e.g. Slack channels); used to
    // prefill importing more.
    scope: link.cronConfig?.scope || {},
    nextSync: enabled
      ? formatDateTime(nextAutoSyncForFrequency(frequency || "daily"))
      : null,
    lastSync: at
      ? {
          at: formatDateTime(at),
          rows: typeof rowCount === "number" ? rowCount : null,
          source: source
            ? source === "cron"
              ? "Auto sync"
              : "Manual sync"
            : null,
        }
      : null,
  };
};

/**
 * One integration's live connection and datasets for its manage popup, plus
 * the per-dataset actions and the user's access. Closes the popup if the
 * integration is disconnected.
 *
 * canSync: the integration supports scheduled / on-demand sync at all.
 * canPublish gates sync, schedule and adding / re-importing; canDelete gates
 * Disconnect.
 */
export const useManagedIntegration = (provider, { name, onClose }) => {
  const userData = useGetQueryData("userData");
  const canPublish = __CheckAccessToModule(
    userData?.user,
    "PUBLISH",
    "integrations",
  );
  const canDelete = __CheckAccessToModule(
    userData?.user,
    "DELETE",
    "integrations",
  );

  // Poll only while a dataset is syncing, so "Syncing…" clears when it ends even
  // when no socket event fires (e.g. a sync that found nothing new).
  const {
    data: conn,
    isLoading,
    error,
  } = useExternalConnectionStatus(provider, {
    refetchInterval: (latest) =>
      (latest?.links || []).some((l) => l.syncInProgress) ? 3000 : false,
  });
  useCloseOnDisconnect(conn, isLoading, onClose);
  const { connect, isConnecting } = useConnectExternalConnection(provider, {
    providerName: name,
  });
  const { data: allTpls } = useGetAllPublishedAndUnpublishedTpls();
  const {
    mutate: updateCron,
    isLoading: isSavingCron,
    isSuccess: cronSaved,
    variables: cronVars,
  } = useUpdateCronConfig(provider);
  const {
    mutate: syncNow,
    isLoading: isSyncStarting,
    variables: syncVars,
  } = useSyncNowExternalConnection(provider, { providerName: name });

  const isConnActive = conn?.status === "active";
  const titleFor = (contentType) =>
    allTpls?.find((t) => t.kp_content_type === contentType)?.general?.content
      ?.title || contentType;
  const datasets = (conn?.links || []).map((link) =>
    toDataset(link, titleFor(link.contentType), {
      isSyncStarting:
        isSyncStarting && syncVars?.contentType === link.contentType,
      isSavingCron: isSavingCron && cronVars?.contentType === link.contentType,
      canPublish,
      isConnActive,
    }),
  );

  return {
    conn,
    isLoading,
    errorMsg: error?.response?.data?.error || error?.message,
    isStale: conn?.status === "stale",
    datasets,
    canSync: !!conn?.supportsIncrementalSync,
    canPublish,
    canDelete,
    connect,
    isConnecting,
    // Which dataset's schedule last saved, for the "Saved" flash.
    savedScheduleFor: cronSaved ? cronVars?.contentType : null,
    sync: (d) => syncNow({ contentType: d.id }),
    // "Off" clears the frequency too, making the dataset one-time.
    applyFrequency: (d, value) =>
      updateCron(
        value === "off"
          ? { contentType: d.id, enabled: false, frequency: null }
          : { contentType: d.id, enabled: true, frequency: value },
      ),
  };
};
